import { Dinero } from "dinero.js";
import React from "react";
import { defaults } from "tsi-common-react/src/apps/checkout/defaults";
import { FinancingModalTriggerBannerMinicart } from "tsi-common-react/src/apps/financing/FinancingModalTrigger";
import { Link } from "tsi-common-react/src/components/Link/index";
import { SrFootnote } from "tsi-common-react/src/components/SrFootnote/index";
import { useMountEffect } from "tsi-common-react/src/hooks/useMountEffect";
import { IBasket } from "tsi-common-react/src/models/catalogue";
import { isoMonths } from "tsi-common-react/src/models/nominals";
import { useAppSelector } from "tsi-common-react/src/store/reducers";
import { listFinancingPlans } from "tsi-common-react/src/utils/financing";
import { format } from "tsi-common-react/src/utils/format";
import { getDinero, getMonthlyPrice } from "tsi-common-react/src/utils/money";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { c, t } from "ttag";

import styles from "./index.module.scss";

interface IFinancePlan {
    termLength: string;
    termSuperscript: string;
    termThreshold: string;
}

// TODO: Consolidate Financing Trigger components into one

const calculateFinancePlan = (basket: IBasket | null): IFinancePlan => {
    const total = basket ? getDinero(basket.total_excl_tax) : getDinero(0);

    const plans = listFinancingPlans();
    const availablePlans = plans.filter((plan) => {
        return plan.threshold.lessThanOrEqual(total);
    });

    if (availablePlans.length) {
        const bestPlan = availablePlans[availablePlans.length - 1];
        const bestPlanLength = isoMonths.unwrap(bestPlan.length);
        const bestPlanSuperscript = bestPlan.superscript;
        const bestPlanThreshold = bestPlan.threshold.toUnit();

        return {
            termLength: String(bestPlanLength),
            termSuperscript: bestPlanSuperscript,
            termThreshold: String(bestPlanThreshold),
        };
    }

    return {
        termLength: "1",
        termSuperscript: "",
        termThreshold: "",
    };
};

const calculateMonthlyPrice = (
    basket: IBasket | null,
    termLength: string,
): Dinero => {
    return basket
        ? getMonthlyPrice(
              getDinero(basket.total_excl_tax),
              parseInt(termLength, 10),
          )
        : getDinero(0.0);
};

const shouldShowFinancing = (
    basket: IBasket | null,
    termThreshold: string,
): boolean => {
    return basket && termThreshold
        ? parseFloat(basket.total_excl_tax) >= parseFloat(termThreshold)
        : false;
};

export const FinancingModalTriggerMinicart = (props: {
    closeModal: () => void;
}): React.ReactNode => {
    const basket = useAppSelector((state) => {
        const checkoutState = state.checkout || defaults;
        return checkoutState.data.basket;
    });

    const financeLink = usePageURL("finance_link");

    const [financePlan, setFinancePlan] = React.useState<IFinancePlan>({
        termLength: "1",
        termSuperscript: "",
        termThreshold: "",
    });

    useMountEffect(() => {
        const plan = calculateFinancePlan(basket);
        setFinancePlan(plan);
    });

    const pricePerMonth = calculateMonthlyPrice(basket, financePlan.termLength);
    const showFinancing = shouldShowFinancing(
        basket,
        financePlan.termThreshold,
    );

    return (
        <div className="basket-popover-financing">
            {showFinancing && (
                <>
                    <div className={styles.basketPopoverFinancingTitle}>
                        {t`or Special Financing`}
                    </div>
                    {/* TODO(i18n): Use jt to keep sentence together with interpolated components instead of splitting into fragments: jt`${pricePerMonth}/mo${sup1} for ${termLength} Months${sup2}` */}
                    <div className={styles.basketPopoverFinancingTerms}>
                        {format.money(pricePerMonth)}/{t`mo`}
                        <sup>
                            <Link href={financeLink}>
                                2
                                <SrFootnote
                                    text={t`Additional information about Terms and Conditions`}
                                />
                            </Link>
                        </sup>
                        <span
                            className={styles.basketPopoverFinancingTermsMedium}
                        >
                            {t`for`}
                        </span>
                        {c("FinancingModalTriggerMinicart")
                            .t`${financePlan.termLength} Months`}
                        <sup>
                            <Link href={financeLink}>
                                {financePlan.termSuperscript}
                                <SrFootnote
                                    text={t`Additional information about Terms and Conditions`}
                                />
                            </Link>
                        </sup>
                    </div>
                </>
            )}
            <FinancingModalTriggerBannerMinicart
                closeModal={props.closeModal}
            />
        </div>
    );
};
